/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { Feature, Map, MapBrowserEvent } from 'ol';
import { platformModifierKeyOnly } from 'ol/events/condition';
import DragBox from 'ol/interaction/DragBox';
import type VectorSource from 'ol/source/Vector';
import { useEffect, useRef } from 'react';

export const isBoxSelectModifier = (event: {
  altKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
}): boolean =>
  platformModifierKeyOnly({
    originalEvent: event,
  } as unknown as MapBrowserEvent<PointerEvent>);

export interface CreateDragBoxSelectOptions {
  sources: VectorSource[];
  onBoxSelect: (features: Feature[], addToSelection: boolean) => void;
}

export interface DragBoxSelect {
  dragBox: DragBox;
  dispose: () => void;
}

export const createDragBoxSelect = ({
  sources,
  onBoxSelect,
}: CreateDragBoxSelectOptions): DragBoxSelect => {
  const dragBox = new DragBox({
    condition: platformModifierKeyOnly,
  });

  let previewedFeatures: Feature[] = [];

  // Preview flags are set silently, so the source needs telling or it keeps its cached frame.
  const repaint = (): void => {
    sources.forEach((source) => source.changed());
  };

  const setPreview = (feature: Feature, isPreviewed: boolean): void => {
    feature.set('boxSelectPreview', isPreviewed, true);
  };

  const clearPreview = (): void => {
    if (!previewedFeatures.length) {
      return;
    }
    previewedFeatures.forEach((feature) => {
      setPreview(feature, false);
    });
    previewedFeatures = [];
    repaint();
  };

  const getIntersectingFeatures = (): Feature[] => {
    const extent = dragBox.getGeometry().getExtent();

    return sources.flatMap((source) =>
      source
        .getFeaturesInExtent(extent)
        .filter((feature) => feature.getGeometry()?.intersectsExtent(extent)),
    );
  };

  dragBox.on('boxstart', () => {
    clearPreview();
  });

  dragBox.on('boxdrag', () => {
    const intersectingFeatures = getIntersectingFeatures();
    const intersectingSet = new Set(intersectingFeatures);
    const previousSet = new Set(previewedFeatures);
    let hasChanges = false;

    previewedFeatures.forEach((feature) => {
      if (!intersectingSet.has(feature)) {
        setPreview(feature, false);
        hasChanges = true;
      }
    });

    intersectingFeatures.forEach((feature) => {
      if (!previousSet.has(feature)) {
        setPreview(feature, true);
        hasChanges = true;
      }
    });

    previewedFeatures = intersectingFeatures;

    if (hasChanges) {
      repaint();
    }
  });

  dragBox.on('boxend', (event) => {
    clearPreview();

    const selectedFeatures = getIntersectingFeatures();

    if (selectedFeatures.length > 0) {
      const originalEvent = event.mapBrowserEvent.originalEvent as PointerEvent;
      const addToSelection = Boolean(originalEvent?.shiftKey);
      onBoxSelect(selectedFeatures, addToSelection);
    }
  });

  return { dragBox, dispose: clearPreview };
};

export interface UseDragBoxSelectOptions {
  map?: Map | null;
  source?: VectorSource;
  active: boolean;
  onBoxSelect?: (features: Feature[], addToSelection: boolean) => void;
}

export const useDragBoxSelect = ({
  map,
  source,
  active,
  onBoxSelect,
}: UseDragBoxSelectOptions): void => {
  const onBoxSelectRef = useRef(onBoxSelect);
  useEffect(() => {
    onBoxSelectRef.current = onBoxSelect;
  });

  useEffect((): (() => void) => {
    if (!map || !source || !active) {
      return (): void => {};
    }

    const { dragBox, dispose } = createDragBoxSelect({
      sources: [source],
      onBoxSelect: (features, addToSelection) =>
        onBoxSelectRef.current?.(features, addToSelection),
    });

    map.addInteraction(dragBox);

    return (): void => {
      dispose();
      map.removeInteraction(dragBox);
    };
  }, [map, source, active]);
};
